import React, { useMemo } from 'react';
import { Alert, Card, Col, DatePicker, Row, Segmented, Select, Space, Spin, Statistic, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getMonthlyProfitLoss,
  type MonthlyProfitLossReport,
  type ProjectPnlTotals,
} from '@/services/financialReports.service';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import companyService from '@/services/company.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { formatMoney, formatNumber, formatPercent, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';
import type { LedgerPageLocationState } from '@/utils/financeVoucherLinks.util';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SECTION_LABEL_KEYS: Record<string, string> = {
  revenue: 'sectionRevenue',
  direct_cost: 'sectionDirectCost',
  overhead: 'sectionOverhead',
  other_income: 'sectionOtherIncome',
};

function amt(v: number | null | undefined): string {
  return formatMoney(v ?? 0, PRINT_PLAIN_AMOUNT_PROFILE);
}

function pctText(v: number | null | undefined): string {
  return v == null ? '—' : formatPercent(v);
}

function signedNumber(v: number, digits: number): string {
  return formatNumber(v, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function variancePct(current: number, prior: number): number | null {
  if (Math.abs(prior) < 0.005) return null;
  return Math.round(((current - prior) / Math.abs(prior)) * 10000) / 100;
}

type RowKind = 'header' | 'account' | 'subtotal' | 'result' | 'margin';

type StatementRow = {
  key: string;
  label: string;
  kind: RowKind;
  values: Array<number | null>;
  total: number | null;
  prior: number | null;
  /** Cost rows: an increase is unfavourable. */
  costRow?: boolean;
  accountId?: string;
};

function buildStatementRows(report: MonthlyProfitLossReport, showAccounts: boolean, t: TFunction): StatementRow[] {
  const rows: StatementRow[] = [];
  const blank = report.months.map(() => null);
  const pushSection = (key: string) => {
    const s = report.sections.find((x) => x.key === key);
    if (!s) return;
    const section = SECTION_LABEL_KEYS[key] ? t(SECTION_LABEL_KEYS[key]) : s.label;
    const costRow = key === 'direct_cost' || key === 'overhead';
    if (showAccounts) {
      rows.push({ key: `h-${key}`, label: section, kind: 'header', values: blank, total: null, prior: null });
      for (const a of s.accounts) {
        rows.push({
          key: `a-${a.account_id}`,
          label: `${a.account_code}  ${a.account_name}`,
          kind: 'account',
          values: a.amounts,
          total: a.total,
          prior: a.prior_total,
          costRow,
          accountId: a.account_id,
        });
      }
    }
    rows.push({
      key: `t-${key}`,
      label: showAccounts ? t('totalSection', { section: section.toLowerCase() }) : section,
      kind: 'subtotal',
      values: s.amounts,
      total: s.total,
      prior: s.prior_total,
      costRow,
    });
  };
  const fromSummary = (pick: (t: ProjectPnlTotals) => number | null) => report.summary.map((m) => pick(m.current));
  const prior = (pick: (t: ProjectPnlTotals) => number | null) => (report.prior_totals ? pick(report.prior_totals) : null);

  pushSection('revenue');
  pushSection('direct_cost');
  rows.push({
    key: 'gp',
    label: t('grossProfitUpper'),
    kind: 'result',
    values: fromSummary((t) => t.gross_profit),
    total: report.totals.gross_profit,
    prior: prior((t) => t.gross_profit),
  });
  rows.push({
    key: 'gpm',
    label: t('grossMarginPct'),
    kind: 'margin',
    values: fromSummary((t) => t.gross_margin_pct),
    total: report.totals.gross_margin_pct,
    prior: prior((t) => t.gross_margin_pct),
  });
  pushSection('overhead');
  pushSection('other_income');
  rows.push({
    key: 'np',
    label: t('netProfitUpper'),
    kind: 'result',
    values: fromSummary((t) => t.net_profit),
    total: report.totals.net_profit,
    prior: prior((t) => t.net_profit),
  });
  rows.push({
    key: 'npm',
    label: t('netMarginPct'),
    kind: 'margin',
    values: fromSummary((t) => t.net_margin_pct),
    total: report.totals.net_margin_pct,
    prior: prior((t) => t.net_margin_pct),
  });
  return rows;
}

type YtdRow = { key: string; label: string; values: number[]; margin?: boolean };

function buildYtdRows(report: MonthlyProfitLossReport, t: TFunction): YtdRow[] {
  return [
    { key: 'ytd-rev', label: t('revenueYtd'), values: report.summary.map((m) => m.ytd.revenue) },
    { key: 'ytd-gp', label: t('grossProfitYtd'), values: report.summary.map((m) => m.ytd.gross_profit) },
    { key: 'ytd-np', label: t('netProfitYtd'), values: report.summary.map((m) => m.ytd.net_profit) },
    {
      key: 'ytd-npm',
      label: t('netMarginPctYtd'),
      values: report.summary.map((m) => m.ytd.net_margin_pct ?? 0),
      margin: true,
    },
  ];
}

const MonthlyProfitLossPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const [searchParams, setSearchParams] = useSearchParams();
  const fromMonth = searchParams.get('from') || undefined;
  const toMonth = searchParams.get('to') || undefined;
  const departmentId = searchParams.get('dept') || undefined;
  const branchId = searchParams.get('branch') || undefined;
  const compare = searchParams.get('compare') === '1';
  const showAccounts = searchParams.get('view') !== 'sections';
  const setParam = (key: string, value?: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const { data: departments = [], isLoading: loadingDepartments } = useDepartmentsForSelection();
  const { data: branches = [], isLoading: loadingBranches } = useQuery({
    queryKey: ['company-branches-selection'],
    queryFn: () => companyService.listBranches({ is_active: true }),
    staleTime: 60000,
  });

  const query = useQuery({
    queryKey: ['fin-monthly-pl', fromMonth, toMonth, departmentId, branchId, compare],
    queryFn: () =>
      getMonthlyProfitLoss({
        from_month: fromMonth,
        to_month: toMonth,
        department_id: departmentId,
        branch_id: branchId,
        compare,
      }),
  });
  const report = query.data;
  const errorMessage = (query.error as any)?.response?.data?.message || (query.error as Error | null)?.message;

  const pickerFrom = fromMonth ?? report?.from_month;
  const pickerTo = toMonth ?? report?.to_month;

  const departmentLabel = departments.find((d) => d.id === departmentId);
  const branchLabel = branches.find((b) => b.id === branchId);
  const filterCaption = [
    departmentLabel ? t('departmentFilter', { code: departmentLabel.department_code, name: departmentLabel.department_name }) : null,
    branchLabel ? t('branchFilter', { code: branchLabel.code, name: branchLabel.name }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const docParams = report
    ? {
        from_month: report.from_month,
        to_month: report.to_month,
        ...(departmentId ? { department_id: departmentId } : {}),
        ...(branchId ? { branch_id: branchId } : {}),
        compare: compare ? '1' : '0',
      }
    : null;

  const body = report ? (
    <MonthlyStatement report={report} showAccounts={showAccounts} compare={compare} printMode={false} filterCaption={filterCaption} />
  ) : null;

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Row justify="space-between" align="middle" gutter={[12, 12]}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t('monthlyProfitLoss')}
          </Title>
          <Text type="secondary">{t('monthlyPlHint')}</Text>
        </Col>
        <Col>
          <ReportDocumentActions reportKey="monthly-profit-loss" params={docParams} />
        </Col>
      </Row>

      <Card size="small">
        <Space wrap size={[16, 8]}>
          <RangePicker
            picker="month"
            allowClear={false}
            format="MMM YYYY"
            value={pickerFrom && pickerTo ? [dayjs(`${pickerFrom}-01`), dayjs(`${pickerTo}-01`)] : null}
            onChange={(v) => {
              if (!v?.[0] || !v?.[1]) return;
              const next = new URLSearchParams(searchParams);
              next.set('from', v[0].format('YYYY-MM'));
              next.set('to', v[1].format('YYYY-MM'));
              setSearchParams(next, { replace: true });
            }}
          />
          <Select
            allowClear
            showSearch
            style={{ minWidth: 240 }}
            placeholder={t('allDepartments')}
            loading={loadingDepartments}
            value={departmentId}
            optionFilterProp="label"
            onChange={(v?: string) => setParam('dept', v)}
            options={departments.map((d) => ({ value: d.id, label: `${d.department_code} — ${d.department_name}` }))}
          />
          <Select
            allowClear
            showSearch
            style={{ minWidth: 200 }}
            placeholder={t('allBranches')}
            loading={loadingBranches}
            value={branchId}
            optionFilterProp="label"
            onChange={(v?: string) => setParam('branch', v)}
            options={branches.map((b) => ({ value: b.id, label: `${b.code} — ${b.name}` }))}
          />
          <Space>
            <Switch checked={compare} onChange={(v) => setParam('compare', v ? '1' : null)} />
            <Text>{t('compareWithLastYear')}</Text>
          </Space>
          <Segmented
            value={showAccounts ? 'accounts' : 'sections'}
            onChange={(v) => setParam('view', v === 'sections' ? 'sections' : null)}
            options={[
              { label: t('showAccounts'), value: 'accounts' },
              { label: t('sectionsOnly'), value: 'sections' },
            ]}
          />
        </Space>
      </Card>

      {query.isFetching && !report ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" tip={tc('loading')} />
        </div>
      ) : query.error ? (
        <Alert type="error" showIcon message={t('couldNotLoadMonthlyPl')} description={errorMessage} />
      ) : (
        <Spin spinning={query.isFetching}>{body}</Spin>
      )}
    </div>
  );
};

const MonthlyStatement: React.FC<{
  report: MonthlyProfitLossReport;
  showAccounts: boolean;
  compare: boolean;
  printMode: boolean;
  filterCaption: string;
}> = ({ report, showAccounts, compare, printMode, filterCaption }) => {
  const { t } = useTranslation('finance');
  const rows = useMemo(() => buildStatementRows(report, showAccounts, t), [report, showAccounts, t]);
  const ytdRows = useMemo(() => buildYtdRows(report, t), [report, t]);
  const firstMonth = report.months[0];
  const lastMonth = report.months[report.months.length - 1];
  const totals = report.totals;
  const p = report.prior_totals;

  const chartData = report.summary.map((m, i) => ({
    month: report.months[i].label,
    revenue: m.current.revenue,
    gross_profit: m.current.gross_profit,
    net_profit: m.current.net_profit,
    net_margin: m.current.net_margin_pct ?? 0,
    prior_net_profit: m.prior?.net_profit ?? null,
  }));

  const valueCell = (v: number | null, row: StatementRow, monthIndex?: number) => {
    if (row.kind === 'header' || v == null) return row.kind === 'margin' ? '—' : '';
    if (row.kind === 'margin') return pctText(v);
    const neg = v < 0 ? { color: '#cf1322' } : undefined;
    const text = amt(v);
    if (row.kind === 'result') return <strong style={neg}>{text}</strong>;
    if (row.kind === 'subtotal') return <strong style={neg}>{text}</strong>;
    if (!printMode && row.accountId && Math.abs(v) > 0.004) {
      const m = monthIndex != null ? report.months[monthIndex] : null;
      const state: LedgerPageLocationState = {
        ledgerType: 'account',
        accountId: row.accountId,
        fromDate: m ? m.from_date : firstMonth.from_date,
        toDate: m ? m.to_date : lastMonth.to_date,
      };
      return (
        <Link to="/fin/reports/ledger" state={state} style={neg}>
          {text}
        </Link>
      );
    }
    return <span style={neg}>{text}</span>;
  };

  const varianceCell = (row: StatementRow) => {
    if (row.kind === 'header' || row.total == null || row.prior == null) return '';
    const diff = row.total - row.prior;
    if (row.kind === 'margin') {
      return <span style={{ color: diff < 0 ? '#cf1322' : undefined }}>{t('ptsDiff', { value: `${diff >= 0 ? '+' : ''}${signedNumber(diff, 2)}` })}</span>;
    }
    const unfavourable = row.costRow ? diff > 0.004 : diff < -0.004;
    return <span style={{ color: unfavourable ? '#cf1322' : diff !== 0 ? '#389e0d' : undefined }}>{amt(diff)}</span>;
  };

  const labelCell = (v: string, row: StatementRow) => {
    if (row.kind === 'header' || row.kind === 'result') return <strong>{v}</strong>;
    if (row.kind === 'subtotal') return <strong style={{ paddingLeft: showAccounts ? 12 : 0 }}>{v}</strong>;
    if (row.kind === 'margin') return <Text type="secondary">{v}</Text>;
    return <span style={{ paddingLeft: 24 }}>{v}</span>;
  };

  const monthWidth = printMode ? undefined : 120;
  const columns: ColumnsType<StatementRow> = [
    {
      title: t('line'),
      dataIndex: 'label',
      fixed: printMode ? undefined : 'left',
      width: printMode ? undefined : 300,
      render: labelCell,
    },
    ...report.months.map((m, i) => ({
      title: m.label,
      key: m.key,
      align: 'right' as const,
      width: monthWidth,
      render: (_: unknown, row: StatementRow) => valueCell(row.values[i], row, i),
    })),
    {
      title: t('total'),
      key: 'total',
      align: 'right' as const,
      width: printMode ? undefined : 140,
      render: (_: unknown, row: StatementRow) => valueCell(row.total, row),
    },
    ...(compare
      ? [
          {
            title: t('lastYearCol'),
            key: 'prior',
            align: 'right' as const,
            width: printMode ? undefined : 130,
            render: (_: unknown, row: StatementRow) =>
              row.kind === 'header' || row.prior == null ? '' : row.kind === 'margin' ? pctText(row.prior) : amt(row.prior),
          },
          {
            title: t('variance'),
            key: 'variance',
            align: 'right' as const,
            width: printMode ? undefined : 130,
            render: (_: unknown, row: StatementRow) => varianceCell(row),
          },
          {
            title: t('varPct'),
            key: 'variance_pct',
            align: 'right' as const,
            width: printMode ? undefined : 90,
            render: (_: unknown, row: StatementRow) =>
              row.kind === 'header' || row.kind === 'margin' || row.total == null || row.prior == null
                ? ''
                : pctText(variancePct(row.total, row.prior)),
          },
        ]
      : []),
  ];

  const ytdColumns: ColumnsType<YtdRow> = [
    {
      title: t('cumulativeYtd'),
      dataIndex: 'label',
      fixed: printMode ? undefined : 'left',
      width: printMode ? undefined : 300,
      render: (v: string) => <Text type="secondary">{v}</Text>,
    },
    ...report.months.map((m, i) => ({
      title: m.label,
      key: m.key,
      align: 'right' as const,
      width: monthWidth,
      render: (_: unknown, row: YtdRow) => {
        const v = row.values[i];
        return <Text type="secondary">{row.margin ? pctText(v) : amt(v)}</Text>;
      },
    })),
  ];

  const statWithPrior = (title: string, current: number, prior: number | undefined, margin?: number | null) => {
    const vp = prior != null ? variancePct(current, prior) : null;
    return (
      <Statistic
        title={title}
        value={margin !== undefined ? `${amt(current)} (${pctText(margin)})` : amt(current)}
        valueStyle={{ color: current < 0 ? '#cf1322' : undefined, fontWeight: 600 }}
        suffix={
          compare && prior != null ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('vsPrior', { amount: amt(prior) })}
              {vp != null ? ` (${vp >= 0 ? '+' : ''}${signedNumber(vp, 1)}%)` : ''}
            </Text>
          ) : undefined
        }
      />
    );
  };

  const scrollX = 300 + report.months.length * 120 + 140 + (compare ? 350 : 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {printMode ? (
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('monthlyProfitLoss')}
          </Title>
          <Text>
            {t('periodFromTo', { from: firstMonth?.label, to: lastMonth?.label })} · {report.currency}
            {filterCaption ? ` · ${filterCaption}` : ''}
            {compare ? t('comparedWithSameMonths') : ''}
          </Text>
        </div>
      ) : (
        <>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              {statWithPrior(t('revenue'), totals.revenue, p?.revenue)}
            </Col>
            <Col xs={24} md={6}>
              {statWithPrior(t('directCosts'), totals.direct_cost, p?.direct_cost)}
            </Col>
            <Col xs={24} md={6}>
              {statWithPrior(t('grossProfit'), totals.gross_profit, p?.gross_profit, totals.gross_margin_pct)}
            </Col>
            <Col xs={24} md={6}>
              {statWithPrior(t('netProfit'), totals.net_profit, p?.net_profit, totals.net_margin_pct)}
            </Col>
          </Row>
          <Card size="small" title={t('monthlyTrend')}>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="amt" tickFormatter={(v: number) => amt(v)} width={100} />
                <YAxis yAxisId="pct" orientation="right" tickFormatter={(v: number) => `${v}%`} width={50} />
                <Tooltip
                  formatter={(value: number, name: string) => (name === t('netMarginPct') ? pctText(value) : amt(value))}
                />
                <Legend />
                <Bar yAxisId="amt" dataKey="revenue" name={t('revenue')} fill="#91caff" />
                <Line yAxisId="amt" type="monotone" dataKey="gross_profit" name={t('grossProfit')} stroke="#1677ff" strokeWidth={2} />
                <Line yAxisId="amt" type="monotone" dataKey="net_profit" name={t('netProfit')} stroke="#389e0d" strokeWidth={2} />
                {compare ? (
                  <Line
                    yAxisId="amt"
                    type="monotone"
                    dataKey="prior_net_profit"
                    name={t('netProfitLastYear')}
                    stroke="#389e0d"
                    strokeDasharray="5 5"
                    dot={false}
                  />
                ) : null}
                <Line yAxisId="pct" type="monotone" dataKey="net_margin" name={t('netMarginPct')} stroke="#fa8c16" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}

      <Table
        size="small"
        bordered
        pagination={false}
        rowKey="key"
        dataSource={rows}
        columns={columns}
        scroll={printMode ? undefined : { x: scrollX }}
        rowClassName={(r) => (r.kind === 'result' ? 'ant-table-row-selected' : '')}
      />

      <Table
        size="small"
        bordered
        pagination={false}
        rowKey="key"
        dataSource={ytdRows}
        columns={ytdColumns}
        scroll={printMode ? undefined : { x: 300 + report.months.length * 120 }}
      />

      <Text type="secondary" style={{ fontSize: 12 }}>
        {t('monthlyPlFootnote', { currency: report.currency })}
        {!printMode ? t('monthlyPlFootnoteClick') : ''}
      </Text>
    </div>
  );
};

export default MonthlyProfitLossPage;
